/**
 * AgentPluginsPage
 *
 * Page for managing plugins per agent. Displays a list of available plugins
 * with their activation status and allows toggling plugins on/off.
 *
 * Validates: Requirements 4.1, 4.6
 * - Requirement 4.1: THE Console SHALL display a list of available plugins for each VirtualAgent.
 * - Requirement 4.6: THE Console SHALL display the current status (enabled/disabled) of each plugin per agent.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { apiGet, apiPost, apiDelete, TENANT_TOKEN_KEY } from '../../api/client';
import { Card, Badge, Button } from '../../design-system';
import { tokens } from '../../design-system/tokens';

/** Plugin metadata from the registry */
interface PluginMetadata {
  id: string;
  name: string;
  description: string;
  version: string;
  category: string;
  icon?: string;
  configSchema?: unknown;
}

/** Agent plugin configuration */
interface AgentPluginConfig {
  id: string;
  config: unknown;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Combined plugin data with status */
interface PluginWithStatus {
  plugin: PluginMetadata;
  agentConfig: AgentPluginConfig | null;
  isEnabled: boolean;
}

/** Agent basic info */
interface Agent {
  id: string;
  name: string;
}

/** Icon for plugins */
function IconPlugin() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Icon for back navigation */
function IconArrowLeft() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Category icon based on plugin category */
function CategoryIcon({ category }: { category: string }) {
  switch (category) {
    case 'productivity':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
      );
    case 'storage':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      );
    case 'communication':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
        </svg>
      );
    case 'analytics':
      return (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      );
    default:
      return <IconPlugin />;
  }
}

/** Toggle switch component */
function ToggleSwitch({
  checked,
  onChange,
  disabled,
  loading,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  loading?: boolean;
}) {
  const handleClick = () => {
    if (!disabled && !loading) {
      onChange(!checked);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading) {
      e.preventDefault();
      onChange(!checked);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled || loading}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      style={{
        position: 'relative',
        width: 48,
        height: 26,
        borderRadius: tokens.borderRadius.full,
        border: 'none',
        backgroundColor: checked ? tokens.colors.primary[600] : tokens.colors.neutral[300],
        cursor: disabled || loading ? 'not-allowed' : 'pointer',
        transition: `background-color ${tokens.transitions.fast}`,
        opacity: disabled ? 0.5 : 1,
        padding: 0,
        outline: 'none',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          left: checked ? 25 : 3,
          width: 20,
          height: 20,
          borderRadius: tokens.borderRadius.full,
          backgroundColor: '#ffffff',
          boxShadow: tokens.shadows.sm,
          transition: `left ${tokens.transitions.fast}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke={tokens.colors.neutral[400]}
            strokeWidth="3"
            style={{ animation: 'spin 1s linear infinite' }}
          >
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
        )}
      </span>
    </button>
  );
}

export function AgentPluginsPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);

  const [agent, setAgent] = useState<Agent | null>(null);
  const [plugins, setPlugins] = useState<PluginWithStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingPlugins, setTogglingPlugins] = useState<Set<string>>(new Set());

  // Load agent info
  const loadAgent = useCallback(async () => {
    if (!agentId || !token) return;
    try {
      const agents = await apiGet<Agent[]>('/api/console/agents', token);
      const found = agents.find((a) => a.id === agentId);
      if (found) {
        setAgent(found);
      } else {
        setError('Agente no encontrado');
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }, [agentId, token]);

  // Load plugins for the agent
  const loadPlugins = useCallback(async () => {
    if (!agentId || !token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiGet<PluginWithStatus[]>(`/api/agents/${agentId}/plugins`, token);
      setPlugins(data);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [agentId, token]);

  useEffect(() => {
    void loadAgent();
    void loadPlugins();
  }, [loadAgent, loadPlugins]);

  // Toggle plugin activation
  const togglePlugin = async (pluginId: string, currentlyEnabled: boolean) => {
    if (!agentId || !token) return;

    setTogglingPlugins((prev) => new Set(prev).add(pluginId));
    setError('');

    try {
      if (currentlyEnabled) {
        // Deactivate plugin
        await apiDelete(`/api/agents/${agentId}/plugins/${pluginId}`, token, {
          successMessage: 'Plugin desactivado',
        });
      } else {
        // Activate plugin
        await apiPost(`/api/agents/${agentId}/plugins/${pluginId}`, { config: {} }, token, {
          successMessage: 'Plugin activado',
        });
      }
      // Reload plugins to get updated state
      await loadPlugins();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setTogglingPlugins((prev) => {
        const next = new Set(prev);
        next.delete(pluginId);
        return next;
      });
    }
  };

  // Group plugins by category
  const pluginsByCategory = useMemo(() => {
    const groups: Record<string, PluginWithStatus[]> = {};
    for (const p of plugins) {
      const cat = p.plugin.category || 'other';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    }
    return groups;
  }, [plugins]);

  const categoryLabels: Record<string, string> = {
    productivity: 'Productividad',
    storage: 'Almacenamiento',
    communication: 'Comunicación',
    analytics: 'Analítica',
    other: 'Otros',
  };

  if (!token) {
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: 24 }}>
      {/* Header with back navigation */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to="/app/agents"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: tokens.colors.neutral[600],
            textDecoration: 'none',
            fontSize: 14,
            marginBottom: 12,
            transition: `color ${tokens.transitions.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = tokens.colors.primary[600])}
          onMouseLeave={(e) => (e.currentTarget.style.color = tokens.colors.neutral[600])}
        >
          <IconArrowLeft />
          Volver a agentes
        </Link>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: tokens.borderRadius.lg,
              backgroundColor: tokens.colors.primary[100],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: tokens.colors.primary[600],
            }}
          >
            <IconPlugin />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, color: tokens.colors.neutral[900] }}>
              Plugins
            </h1>
            {agent && (
              <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[500] }}>
                Agente: <strong>{agent.name}</strong>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: 16,
            borderRadius: tokens.borderRadius.md,
            backgroundColor: tokens.colors.error.light,
            color: tokens.colors.error.dark,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
            color: tokens.colors.neutral[500],
          }}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            style={{ animation: 'spin 1s linear infinite', marginRight: 12 }}
          >
            <circle cx="12" cy="12" r="10" strokeDasharray="31.4 31.4" strokeLinecap="round" />
          </svg>
          Cargando plugins...
        </div>
      )}

      {/* Empty state */}
      {!loading && plugins.length === 0 && !error && (
        <Card variant="outlined" padding="lg">
          <div style={{ textAlign: 'center', color: tokens.colors.neutral[500] }}>
            <IconPlugin />
            <p style={{ marginTop: 12 }}>No hay plugins disponibles en el sistema.</p>
          </div>
        </Card>
      )}

      {/* Plugin list by category */}
      {!loading && plugins.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {Object.entries(pluginsByCategory).map(([category, categoryPlugins]) => (
            <div key={category}>
              <h2
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: tokens.colors.neutral[500],
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: 12,
                }}
              >
                {categoryLabels[category] || category}
              </h2>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {categoryPlugins.map((p) => (
                  <Card key={p.plugin.id} variant="outlined" padding="md">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 16,
                      }}
                    >
                      {/* Plugin icon */}
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: tokens.borderRadius.lg,
                          backgroundColor: p.isEnabled
                            ? tokens.colors.primary[50]
                            : tokens.colors.neutral[100],
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: p.isEnabled
                            ? tokens.colors.primary[600]
                            : tokens.colors.neutral[400],
                          flexShrink: 0,
                          transition: `all ${tokens.transitions.fast}`,
                        }}
                      >
                        <CategoryIcon category={p.plugin.category} />
                      </div>

                      {/* Plugin info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <h3
                            style={{
                              margin: 0,
                              fontSize: 16,
                              fontWeight: 600,
                              color: tokens.colors.neutral[900],
                            }}
                          >
                            {p.plugin.name}
                          </h3>
                          <Badge variant={p.isEnabled ? 'success' : 'neutral'} size="sm">
                            {p.isEnabled ? 'Activo' : 'Inactivo'}
                          </Badge>
                          <span
                            style={{
                              fontSize: 12,
                              color: tokens.colors.neutral[400],
                              fontFamily: tokens.typography.fontFamily.mono,
                            }}
                          >
                            v{p.plugin.version}
                          </span>
                        </div>
                        <p
                          style={{
                            margin: 0,
                            fontSize: 14,
                            color: tokens.colors.neutral[600],
                            lineHeight: 1.5,
                          }}
                        >
                          {p.plugin.description}
                        </p>
                      </div>

                      {/* Toggle switch */}
                      <div style={{ flexShrink: 0 }}>
                        <ToggleSwitch
                          checked={p.isEnabled}
                          onChange={() => togglePlugin(p.plugin.id, p.isEnabled)}
                          loading={togglingPlugins.has(p.plugin.id)}
                        />
                      </div>
                    </div>

                    {/* Configuration hint when enabled */}
                    {p.isEnabled && p.plugin.configSchema !== undefined && p.plugin.configSchema !== null && (
                      <div
                        style={{
                          marginTop: 12,
                          paddingTop: 12,
                          borderTop: `1px solid ${tokens.colors.neutral[200]}`,
                        }}
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/app/agents/${agentId}/plugins/${p.plugin.id}/config`)}
                        >
                          Configurar plugin
                        </Button>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CSS for spin animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default AgentPluginsPage;
